/**
 * transactions.ts — the unassigned pool, read once for every picker on the canvas.
 *
 * THE KEY AND THE RPC ARE `TransactionSelector`'S, UNCHANGED: `['unassigned-transactions', search,
 * type]` over `get_unassigned_transactions(search_query, limit_count, type_filter)`. That matters
 * because every writer in the app already invalidates the `['unassigned-transactions']` PREFIX
 * after linking a payment — nine sheets do it, and the whole point of linking is that the
 * transaction leaves the queue. A new key here would be a queue that never refreshes.
 *
 * SEARCH IS SERVER-SIDE HERE AND CLIENT-SIDE IN THE PICKER, AND BOTH ARE RIGHT. The RPC caps the
 * result at fifty rows, so a search term has to reach it or an old payment beyond the cap can
 * never be found; `pickerState`'s every-term-anywhere filter then narrows what came back. Passing
 * the typed text into the key is what the selector did and it is kept.
 */

import { supabase } from '@/integrations/supabase/client';

import type { PickableTransaction } from './transactionPick';

/** Prefix form — what a writer clears when a payment leaves the pool. */
export const UNASSIGNED_TRANSACTIONS_KEY = ['unassigned-transactions'] as const;

export const unassignedTransactionsKey = (search: string, type: 'CREDIT' | 'DEBIT') =>
    ['unassigned-transactions', search, type] as const;

export const transactionKey = (transactionId: number | null) =>
    ['transaction', transactionId] as const;

export async function fetchUnassignedTransactions(
    search: string,
    type: 'CREDIT' | 'DEBIT',
): Promise<PickableTransaction[]> {
    const { data, error } = await supabase.rpc('get_unassigned_transactions', {
        search_query: search || null,
        limit_count: 50,
        type_filter: type,
    });
    if (error) throw error;
    return (data || []) as PickableTransaction[];
}

/**
 * The transaction a row already holds.
 *
 * A LINKED TRANSACTION IS NOT IN THE UNASSIGNED POOL, by definition — so without this read the
 * picker's trigger would have nothing to print for a row that has been filled in for months.
 */
export async function fetchTransaction(
    transactionId: number | null,
): Promise<PickableTransaction | null> {
    if (!transactionId) return null;
    const { data, error } = await supabase
        .from('transactions')
        .select('id, amount, transaction_at, description, type, currency')
        .eq('id', transactionId)
        .single();
    if (error) throw error;
    return data as PickableTransaction;
}

/** Record a payment that is not in the feed yet, and hand back the row so it can be selected. */
export async function createTransaction({
    amount,
    dated,
    description,
    type,
}: {
    amount: string;
    dated: string;
    description: string;
    type: 'CREDIT' | 'DEBIT';
}): Promise<PickableTransaction> {
    const { data, error } = await supabase
        .from('transactions')
        .insert({
            amount: parseFloat(amount),
            transaction_at: dated,
            description: description.trim() || null,
            type,
        })
        .select('id, amount, transaction_at, description, type, currency')
        .single();
    if (error) throw error;
    return data as PickableTransaction;
}
